import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, TEAM_COLORS, money, useUrlSearch, SearchInput, matchText } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, Mail } from "lucide-react";

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const DEFAULT_AVAILABILITY = {
  mon: { enabled: true, start: "09:00", end: "17:00" },
  tue: { enabled: true, start: "09:00", end: "17:00" },
  wed: { enabled: true, start: "09:00", end: "17:00" },
  thu: { enabled: true, start: "09:00", end: "17:00" },
  fri: { enabled: true, start: "09:00", end: "17:00" },
  sat: { enabled: false, start: "09:00", end: "13:00" },
  sun: { enabled: false, start: "09:00", end: "13:00" },
};

const empty = {
  name: "", email: "", phone: "", title: "", role: "Cleaner",
  hourly_rate: "", color: TEAM_COLORS[0], password: "", active: true,
  availability: DEFAULT_AVAILABILITY, sms_digest: true, sms_change_alerts: true,
};

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useUrlSearch();

  const filtered = items.filter((m) =>
    matchText(search, m.name, m.email, m.position, m.title, m.phone));

  const load = () => api.get("/team").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (m) => {
    setForm({
      name: m.name, email: m.email, phone: m.phone || "", title: m.title || "",
      role: m.position || "Cleaner", hourly_rate: m.hourly_rate ?? "",
      color: m.color || TEAM_COLORS[0], password: "", active: m.active,
      availability: m.availability || DEFAULT_AVAILABILITY,
      sms_digest: m.sms_digest !== false, sms_change_alerts: m.sms_change_alerts !== false,
    });
    setEditing(m.id); setOpen(true);
  };

  const setDay = (dayKey, field, value) => {
    setForm((f) => ({
      ...f,
      availability: {
        ...f.availability,
        [dayKey]: { ...f.availability[dayKey], [field]: value },
      },
    }));
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    try {
      if (editing) {
        const payload = {
          name: form.name, phone: form.phone, title: form.title, role: form.role,
          hourly_rate: Number(form.hourly_rate) || 0, color: form.color,
          availability: form.availability, active: form.active,
          sms_digest: form.sms_digest, sms_change_alerts: form.sms_change_alerts,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/team/${editing}`, payload);
      } else {
        const { data } = await api.post("/team", { ...form, hourly_rate: Number(form.hourly_rate) || 0, send_welcome: sendWelcome });
        const w = data.welcome_email;
        const t = w?.sms ? (w.sms.status === "test_mode" ? " · text logged (live sending off)" : w.sms.error ? " · text failed" : " · welcome text sent") : "";
        if (w && !w.sent) toast.warning(`Added, but the welcome email couldn't be sent (${w.reason})${t}. Use the envelope button to retry.`);
        else toast.success(w ? `Team member added · welcome email sent to ${data.email}${t}` : "Team member added");
      }
      if (editing) toast.success("Team member updated");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendWelcomeTo = async (m) => {
    try {
      const { data } = await api.post(`/team/${m.id}/welcome`);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unlock = async (m) => {
    try {
      const { data } = await api.post(`/team/${m.id}/unlock`);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    await api.delete(`/team/${id}`);
    toast.success("Team member removed");
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader
        eyebrow="Your People"
        title="Team"
        description="Manage your cleaners and give them their own logins to see assigned jobs."
        action={
          isAdmin && (
            <Button onClick={openNew} data-testid="add-team-button" className="rounded-sm gap-2">
              <Plus className="w-4 h-4" /> New Member
            </Button>
          )
        }
      />

      {items.length > 0 && (
        <div className="mb-6">
          <SearchInput value={search} onChange={setSearch} placeholder="Search team…" testid="team-search" />
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          title="No team members yet"
          description="Add cleaners so you can assign them to scheduled jobs."
          action={isAdmin && <Button onClick={openNew} className="rounded-sm">Add Member</Button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" description={`No team members match “${search}”.`} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="label-caps">Name</TableHead>
              <TableHead className="label-caps">Role</TableHead>
              <TableHead className="label-caps">Rate</TableHead>
              <TableHead className="label-caps">Jobs</TableHead>
              <TableHead className="label-caps">Colour</TableHead>
              <TableHead className="label-caps">Status</TableHead>
              {isAdmin && <TableHead className="text-right label-caps">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((m) => (
              <TableRow key={m.id} data-testid={`team-row-${m.id}`}>
                <TableCell className="py-4">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-sm text-muted-foreground">{m.email}</div>
                </TableCell>
                <TableCell>{m.position || m.title || "Cleaner"}</TableCell>
                <TableCell>{m.hourly_rate ? `${money(m.hourly_rate)}/hr` : "—"}</TableCell>
                <TableCell data-testid={`team-jobs-${m.id}`}>{m.job_count ?? 0}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 rounded-sm border border-border" style={{ backgroundColor: m.color || "#8a817c" }} />
                  </span>
                </TableCell>
                <TableCell>
                  <span className="label-caps text-muted-foreground">{m.active ? "Active" : "Inactive"}</span>
                  {(m.sms_digest === false || m.sms_change_alerts === false) && (
                    <div className="text-[10px] uppercase tracking-wider text-destructive mt-1" data-testid={`team-texts-off-${m.id}`}>
                      {m.sms_digest === false && m.sms_change_alerts === false ? "All texts off" : m.sms_digest === false ? "Job list off" : "Alerts off"}
                    </div>
                  )}
                  {m.locked_until && (
                    <div className="mt-1.5 flex items-center gap-2" data-testid={`team-locked-${m.id}`}>
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-destructive"><Lock className="w-3 h-3" /> Locked until {new Date(m.locked_until).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}</span>
                      {isAdmin && <button onClick={() => unlock(m)} data-testid={`unlock-team-${m.id}`} className="text-xs text-brand hover:underline">Unlock</button>}
                    </div>
                  )}
                </TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <button onClick={() => sendWelcomeTo(m)} title={m.welcome_sent_at ? `Resend welcome email (last sent ${new Date(m.welcome_sent_at).toLocaleDateString("en-AU")})` : "Send welcome email"} data-testid={`welcome-team-${m.id}`} className="p-2 text-muted-foreground hover:text-primary">
                      <Mail className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(m)} data-testid={`edit-team-${m.id}`} className="p-2 text-muted-foreground hover:text-primary">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(m.id)} data-testid={`delete-team-${m.id}`} className="p-2 text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit Team Member" : "New Team Member"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Name</Label>
                <Input value={form.name} onChange={set("name")} data-testid="team-name" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Role</Label>
                <Input value={form.role} onChange={set("role")} data-testid="team-role" placeholder="Cleaner / Team Lead" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Email {editing && "(cannot change)"}</Label>
              <Input value={form.email} onChange={set("email")} data-testid="team-email" disabled={!!editing} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Hourly rate (AUD)</Label>
                <Input type="number" value={form.hourly_rate} onChange={set("hourly_rate")} data-testid="team-rate" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Phone</Label>
                <Input value={form.phone} onChange={set("phone")} data-testid="team-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Schedule colour</Label>
              <div className="flex flex-wrap gap-2" data-testid="team-color-picker">
                {TEAM_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    style={{ backgroundColor: c }}
                    className={`w-8 h-8 rounded-sm border-2 transition-transform ${form.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Availability (hours per day)</Label>
              <div className="border border-border/60 rounded-sm divide-y divide-border/60" data-testid="team-availability">
                {DAYS.map((d) => {
                  const av = form.availability?.[d.key] || { enabled: false, start: "09:00", end: "17:00" };
                  return (
                    <div key={d.key} className="flex items-center gap-3 p-2.5" data-testid={`avail-${d.key}`}>
                      <div className="w-10 text-sm font-medium">{d.label}</div>
                      <Switch checked={av.enabled} onCheckedChange={(v) => setDay(d.key, "enabled", v)} data-testid={`avail-${d.key}-enabled`} />
                      {av.enabled ? (
                        <div className="flex items-center gap-2 ml-auto">
                          <Input type="time" value={av.start} onChange={(e) => setDay(d.key, "start", e.target.value)} className="w-28" data-testid={`avail-${d.key}-start`} />
                          <span className="text-muted-foreground text-sm">–</span>
                          <Input type="time" value={av.end} onChange={(e) => setDay(d.key, "end", e.target.value)} className="w-28" data-testid={`avail-${d.key}-end`} />
                        </div>
                      ) : (
                        <span className="ml-auto text-sm text-muted-foreground">Unavailable</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">{editing ? "Reset password" : "Password"}</Label>
              <Input type="password" autoComplete="new-password" value={form.password} onChange={set("password")} data-testid="team-password"
                placeholder={editing ? "Type a new password to reset (min 8 characters)" : "Optional — leave blank so they choose their own"} />
              {!editing && (
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="team-send-welcome">
                  <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} className="accent-primary w-4 h-4" data-testid="team-send-welcome-checkbox" />
                  Email and text them a welcome with a link to set their password (valid 7 days)
                </label>
              )}
              {editing && <p className="text-xs text-muted-foreground" data-testid="team-password-hint">Leave blank to keep their current password. Resetting signs them out on all devices — share the new password with them.</p>}
            </div>
            <div className="flex items-center justify-between pt-2">
              <Label className="label-caps text-muted-foreground">Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="team-active" />
            </div>
            <div className="border border-border/60 rounded-sm px-4 py-2 divide-y divide-border/60">
              <div className="flex items-center justify-between py-2">
                <div className="text-sm">Evening job list text <span className="text-xs text-muted-foreground">(6:00 PM)</span></div>
                <Switch checked={form.sms_digest} onCheckedChange={(v) => setForm({ ...form, sms_digest: v })} data-testid="team-sms-digest" />
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="text-sm">Instant change alert texts</div>
                <Switch checked={form.sms_change_alerts} onCheckedChange={(v) => setForm({ ...form, sms_change_alerts: v })} data-testid="team-sms-alerts" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-team-button" className="rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
